import { useState, useCallback } from 'react'
import { Input } from '../Input/Input.jsx'
import styles from './RangeInput.module.scss'

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Snap a raw value to the nearest step boundary, then clamp within [min, max].
 * @param {number} v
 * @param {number} min
 * @param {number} max
 * @param {number} step
 * @returns {number}
 */
function snapAndClamp(v, min, max, step) {
  const snapped = Math.round((v - min) / step) * step + min
  return Math.min(max, Math.max(min, snapped))
}

// ── DraftInput ────────────────────────────────────────────────────────────────

/**
 * Single numeric input with a local "in-flight" draft string while the user is
 * typing. The parent value is only updated on blur — this prevents emitting a
 * new lo/hi on every keystroke and allows the user to type "1200000" one
 * character at a time without the slider jumping on each digit.
 *
 * @param {object}  props
 * @param {number}  props.value         - Controlled numeric value from parent.
 * @param {number}  props.min
 * @param {number}  props.max
 * @param {number}  props.step
 * @param {(v: number) => void} props.onCommit  - Called on blur with clamped value.
 * @param {string}  [props.prefix]
 * @param {React.ReactNode} [props.suffix]
 * @param {boolean} [props.isActive]
 * @param {boolean} [props.disabled]
 * @param {string}  [props.id]
 */
function DraftInput({ value, min, max, step, onCommit, prefix, suffix, isActive, disabled, id }) {
  // null = not in edit mode, show parent value
  const [draft, setDraft] = useState(null)

  const displayValue = draft !== null ? draft : String(value)

  const handleFocus = useCallback(() => {
    setDraft(String(value))
  }, [value])

  const handleChange = useCallback((raw) => {
    // Allow empty string and any numeric characters while typing
    setDraft(raw)
  }, [])

  const handleBlur = useCallback(() => {
    const parsed = parseFloat(String(draft).replace(/\s/g, '').replace(',', '.'))
    const committed = isNaN(parsed) ? value : snapAndClamp(parsed, min, max, step)
    setDraft(null)
    onCommit(committed)
  }, [draft, value, min, max, step, onCommit])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.target.blur()
      return
    }
    if (e.key === 'Escape') {
      setDraft(null)
      e.target.blur()
      return
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      // Prevent the browser default on type="text" which moves the cursor
      // to the start (ArrowUp) or end (ArrowDown) of the text.
      e.preventDefault()
      const raw = parseFloat(String(draft ?? value).replace(/\s/g, '').replace(',', '.'))
      const base = isNaN(raw) ? value : raw
      const decimals = String(step).split('.')[1]?.length ?? 0
      const stepped = parseFloat((base + step * (e.key === 'ArrowUp' ? 1 : -1)).toFixed(decimals))
      const clamped = snapAndClamp(stepped, min, max, step)
      if (draft !== null) {
        // In draft mode: update the visible string; commit will happen on blur.
        setDraft(String(clamped))
      } else {
        // Not yet in draft mode: commit immediately.
        onCommit(clamped)
      }
    }
  }, [draft, value, min, max, step, onCommit])

  return (
    <Input
      id={id}
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      prefix={prefix}
      suffix={suffix}
      isActive={isActive}
      disabled={disabled}
    />
  )
}

// ── RangeInput ────────────────────────────────────────────────────────────────

/**
 * Dual numeric input for a range filter (min / max).
 *
 * Renders two `Input` primitives side by side with "od" / "do" prefixes.
 * Values are committed on blur with snap-to-step and clamp to [min, max].
 * Always emits sorted [lo, hi] — if the user types a lo > hi, they are swapped.
 *
 * @param {object}  props
 * @param {[number, number]}  props.value         - Controlled [lo, hi].
 * @param {[number, number]}  props.defaultValue  - Global [min, max] bounds.
 * @param {number}  [props.step=1]
 * @param {(v: [number, number]) => void} props.onChange
 * @param {React.ReactNode} [props.suffix]        - Unit label, e.g. "zł", "m²"
 * @param {boolean} [props.isActive=false]
 * @param {boolean} [props.disabled=false]
 * @param {string}  [props.id]                    - Base id; "-lo" / "-hi" appended.
 */
export function RangeInput({
  value,
  defaultValue,
  step = 1,
  onChange,
  suffix,
  isActive = false,
  disabled = false,
  id,
}) {
  const [min, max] = defaultValue
  const [lo, hi] = value

  const commitLo = useCallback(
    (newLo) => onChange([Math.min(newLo, hi), Math.max(newLo, hi)]),
    [hi, onChange],
  )

  const commitHi = useCallback(
    (newHi) => onChange([Math.min(lo, newHi), Math.max(lo, newHi)]),
    [lo, onChange],
  )

  return (
    <div className={styles.root}>
      <DraftInput
        id={id ? `${id}-lo` : undefined}
        value={lo}
        min={min}
        max={max}
        step={step}
        onCommit={commitLo}
        prefix="od"
        suffix={suffix}
        isActive={isActive}
        disabled={disabled}
      />
      <DraftInput
        id={id ? `${id}-hi` : undefined}
        value={hi}
        min={min}
        max={max}
        step={step}
        onCommit={commitHi}
        prefix="do"
        suffix={suffix}
        isActive={isActive}
        disabled={disabled}
      />
    </div>
  )
}
